// Notas faltantes para aprovação — visão por ALUNO (Portal da Equipe e Central de Notas).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Download, FileSpreadsheet, Loader2, RefreshCw, Search, Send } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { C, EmptyState, FilterSelect, StatusBadge } from '@/design-system/dna';
import { ClassMultiSelect } from '@/pages/AcademicoRelatorios/reporting/ClassMultiSelect';
import { SendFamilyDialog } from './SendFamilyDialog';

const TONE = { verde: 'success', amarelo: 'warning', laranja: 'warning', vermelho: 'danger' };
const DOT = { verde: C.green, amarelo: C.amber, laranja: C.orange, vermelho: '#E2412B' };
const SIT = [['all', 'Todas as situações'], ['vermelho', 'Acima de 10'], ['laranja', 'Entre 6,1 e 10'], ['amarelo', 'Até 6,0'], ['verde', 'Já atingiu']];
const TRI = ['1º Trimestre', '2º Trimestre', '3º Trimestre'];
const n = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 }));
const ALL = 'all';

export function useFaltantes(endpoint, params, { poll = 60000, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify(params);
  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try { const { data: d } = await api.get(endpoint, { params: JSON.parse(key) }); setData(d); }
    catch { setData((d) => d || { students: [], summary: { alunos: 0, vermelho: 0, laranja: 0, amarelo: 0, verde: 0 } }); }
    finally { setLoading(false); }
  }, [endpoint, key, enabled]);
  useEffect(() => { load(); if (!enabled || !poll) return undefined; const t = setInterval(load, poll); return () => clearInterval(t); }, [load, enabled, poll]);
  return { data, loading, reload: load };
}

function Detail({ st }) {
  return (
    <Table data-testid={`faltantes-detail-${st.student_id}`}>
      <TableHeader><TableRow>{['Disciplina', '1º Tri', '2º Tri', '3º Tri', 'Média atual', 'Nota necessária para 6,0', 'Situação'].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {st.subjects.map((x) => (
          <TableRow key={x.subject_id} data-testid={`faltantes-subject-${st.student_id}-${x.subject_id}`}>
            <TableCell className="font-semibold">{x.subject_name}</TableCell>
            {['t1', 't2', 't3'].map((t, i) => <TableCell key={t} className="tabular-nums">{n(x[t])}{x[t] == null && x.parcial?.[TRI[i]] && <span className="ml-1 text-[10px]" style={{ color: C.muted }}>parcial</span>}</TableCell>)}
            <TableCell className="tabular-nums">{n(x.media_atual)}</TableCell>
            <TableCell>{x.status === 'verde' ? '—' : <>
              <span className="text-[15px] font-extrabold tabular-nums" style={{ color: DOT[x.status] }}>{n(x.necessaria)}</span>
              <span className="ml-1.5 text-[10.5px]" style={{ color: C.muted }}>{x.restantes.length > 1 ? `em cada um dos ${x.restantes.length} tri.` : x.restantes[0]}{x.necessaria_direta !== x.necessaria ? ` · conta direta ${n(x.necessaria_direta)}` : ''}</span>
            </>}</TableCell>
            <TableCell className="max-w-[260px]"><StatusBadge tone={TONE[x.status]}>{x.status_label}</StatusBadge></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function FaltantesAprovacaoDialog({ open, onOpenChange, scope = 'central', year: initialYear, defaultClassId }) {
  const endpoint = scope === 'teacher' ? '/teacher-portal/faltantes-aprovacao' : '/central-notas/faltantes-aprovacao';
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(initialYear || thisYear);
  const [f, setF] = useState({ cycle: ALL, segment: ALL, classes: defaultClassId ? [defaultClassId] : [], status: ALL, q: '' });
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sendTo, setSendTo] = useState(null);
  const [exporting, setExporting] = useState(null);
  const { data, loading, reload } = useFaltantes(endpoint, { year }, { enabled: open });
  useEffect(() => { if (open && defaultClassId) setF((x) => ({ ...x, classes: [defaultClassId] })); }, [open, defaultClassId]);

  const all = data?.students || [];
  const classOpts = useMemo(() => [...new Map(all.map((s) => [s.class_id, { id: s.class_id, name: s.class_name, segment: s.segment, cycle: s.cycle, year }])).values()]
    .filter((c) => (f.cycle === ALL || c.cycle === f.cycle) && (f.segment === ALL || c.segment === f.segment))
    .sort((a, b) => a.name.localeCompare(b.name)), [all, f.cycle, f.segment, year]);
  const cycles = data?.cycles || ['Educação Infantil', 'Fundamental I', 'Fundamental II', 'Ensino Médio'];
  const segments = [...new Set(all.map((s) => s.segment).filter(Boolean))];
  const q = f.q.trim().toLowerCase();
  const scoped = all.filter((s) => (f.cycle === ALL || s.cycle === f.cycle) && (f.segment === ALL || s.segment === f.segment) && (!f.classes.length || f.classes.includes(s.class_id))
    && (!q || s.student_name.toLowerCase().includes(q) || String(s.registration || '').toLowerCase() === q));
  const list = scoped.filter((s) => f.status === ALL || s.status === f.status);
  const counts = Object.fromEntries(['vermelho', 'laranja', 'amarelo', 'verde'].map((k) => [k, scoped.filter((s) => s.status === k).length]));
  const set = (k) => (v) => { setF((x) => ({ ...x, [k]: v })); setSelected(null); };
  const closeSend = useCallback(() => setSendTo(null), []);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const p = { year, format };
      if (selected) p.student_id = selected.student_id;
      else {
        if (f.cycle !== ALL) p.cycle = f.cycle;
        if (f.segment !== ALL) p.segment = f.segment;
        if (f.classes.length) p.class_ids = f.classes.join(',');
        if (f.status !== ALL) p.status = f.status;
        if (q) p.student = f.q.trim();
      }
      const { data: blob } = await api.get(endpoint, { params: p, responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = selected ? `acompanhamento_${selected.student_name.split(' ')[0].toLowerCase()}_${year}.${format}` : `notas_faltantes_aprovacao_${year}.${format}`;
      a.click();
    } finally { setExporting(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1180px] w-[96vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="faltantes-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold" style={{ color: C.ink }}>Notas faltantes para aprovação</DialogTitle>
          <DialogDescription>Uma linha por aluno · nota necessária para média final 6,0 pelo Motor Oficial de Médias · atualiza a cada minuto.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-start gap-2" data-testid="faltantes-filters">
          <FilterSelect id="faltantes-ano" value={year} onChange={(v) => { setYear(Number(v)); setSelected(null); }} items={[thisYear - 1, thisYear, thisYear + 1].map((y) => ({ value: y, label: `Ano ${y}` }))} />
          <FilterSelect id="faltantes-segmento" value={f.segment} onChange={set('segment')} items={[{ value: ALL, label: 'Todos os segmentos' }, ...segments.map((s) => ({ value: s, label: s }))]} />
          <FilterSelect id="faltantes-ciclo" value={f.cycle} onChange={(v) => { setF((x) => ({ ...x, cycle: v, classes: [] })); setSelected(null); }} items={[{ value: ALL, label: 'Todos os ciclos' }, ...cycles.map((c) => ({ value: c, label: c }))]} />
          <FilterSelect id="faltantes-situacao" value={f.status} onChange={set('status')} items={SIT.map(([value, label]) => ({ value, label }))} />
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: C.muted }} />
            <Input value={f.q} onChange={(e) => set('q')(e.target.value)} placeholder="Aluno: nome ou matrícula" className="h-10 w-[230px] pl-9" data-testid="faltantes-search" />
          </div>
          <ClassMultiSelect options={classOpts} selected={f.classes} onChange={set('classes')} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 text-[12px]" data-testid="faltantes-summary">
            {['vermelho', 'laranja', 'amarelo', 'verde'].map((k) => <span key={k} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-bold" style={{ borderColor: C.line, color: C.ink }}><span className="h-2 w-2 rounded-full" style={{ background: DOT[k] }} />{counts[k]}</span>)}
            <span style={{ color: C.muted }} data-testid="faltantes-count">{list.length} aluno(s){selected ? ` · selecionado: ${selected.student_name}` : ''}</span>
            {selected && <button type="button" className="font-semibold" style={{ color: C.purple }} onClick={() => setSelected(null)} data-testid="faltantes-clear-selected">limpar seleção</button>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="faltantes-refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Atualizar</Button>
            <Button variant="outline" size="sm" onClick={() => exportFile('pdf')} disabled={!!exporting} data-testid="faltantes-export-pdf">{exporting === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF</Button>
            <Button variant="outline" size="sm" onClick={() => exportFile('xlsx')} disabled={!!exporting} data-testid="faltantes-export-xlsx">{exporting === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel</Button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          {!data ? <p className="flex items-center gap-2 py-10 text-sm" style={{ color: C.muted }}><Loader2 className="h-4 w-4 animate-spin" /> Calculando pelo motor de médias…</p>
            : !list.length ? <EmptyState title="Nenhum aluno neste filtro" testid="faltantes-empty">Ajuste os filtros ou aguarde novas notas lançadas.</EmptyState> : (
              <Table data-testid="faltantes-table">
                <TableHeader><TableRow>{['Aluno', 'Turma', 'Disciplinas em atenção', 'Situação geral', ''].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {list.map((s) => (
                    <React.Fragment key={s.student_id}>
                      <TableRow data-testid={`faltantes-row-${s.student_id}`} onClick={() => setSelected(selected?.student_id === s.student_id ? null : s)}
                        className="cursor-pointer" style={selected?.student_id === s.student_id ? { background: '#F7F1FA' } : undefined}>
                        <TableCell><p className="font-semibold">{s.student_name}</p><p className="text-[11px]" style={{ color: C.muted }}>{s.registration ? `Matrícula ${s.registration}` : '—'}</p></TableCell>
                        <TableCell>{s.class_name}</TableCell>
                        <TableCell data-testid={`faltantes-attention-${s.student_id}`}><b>{s.attention}</b> <span style={{ color: C.muted }}>de {s.subjects.length}</span></TableCell>
                        <TableCell><StatusBadge tone={TONE[s.status]} testid={`faltantes-status-${s.student_id}`}>{s.status_label}</StatusBadge></TableCell>
                        <TableCell className="whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm" onClick={() => setExpanded(expanded === s.student_id ? null : s.student_id)} data-testid={`faltantes-details-${s.student_id}`}>
                            Ver detalhes <ChevronDown className={`h-4 w-4 transition-transform ${expanded === s.student_id ? 'rotate-180' : ''}`} />
                          </Button>
                          <Button size="sm" className="ml-2" onClick={() => setSendTo(s)} data-testid={`faltantes-send-${s.student_id}`}><Send className="h-4 w-4" /> Enviar à família</Button>
                        </TableCell>
                      </TableRow>
                      {expanded === s.student_id && <TableRow><TableCell colSpan={5} className="bg-[#FBF9FD] p-3"><Detail st={s} /></TableCell></TableRow>}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
        </div>
        <SendFamilyDialog student={sendTo} year={year} scope={scope} endpoint={endpoint} onClose={closeSend} />
      </DialogContent>
    </Dialog>
  );
}
